import { createContext, ReactNode } from 'react';
import { PDFManager } from 'PDF/services';
import { PresentationManager } from 'Presentation/services/PresentationManager';
import EditorManager from 'Editor/services/EditorManager';
import { useObject } from '_common/hooks';
import { Editor } from '@tiptap/core';

/**
 * Assumes that an admin/owner can do everything, the remaining restrictions are:
 * @property canAccess - has access permission to the Presentation
 * @property canEdit - is allowed to edit content
 * @property canAccessVersionHistory - has access permission to the version history feature
 * @property canSaveVersion - is allowed to save a new version
 * @property canRestoreVersion - is allowed to restore a version
 * @property canShare - is allowed to share
 * @property canChangeStatus - is allowed to change the status
 * @property canComment - is allowed to create comments (comment permission)
 * @property canDeleteComment - need to be the author of specified comment
 * @property canEditComment - need to be the author of specified comment
 * @property canResolveComment - need to be the author of specified comment
 * @property canChangeCommentPriority - need to be the author of specidied comment or document owner
 * @property canEditCommentReply - need to be the author of specified comment reply
 * @property canDeleteCommentReply - need to be the author of specidied comment reply
 * @property canAccessAuditLog - is allowed to access the audit log
 * @property canAccessTasks - not integrated version, if it is, only acessable if tasks are present in document
 * @property canCreateTask - is allowed to create tasks (comment permission or document owner)
 * @property canDeleteTask - need to be the author of specified task or document owner
 * @property canChangeTaskStatus - is allowed to change the task status
 * @property canEditTask - need to be the author of specified task
 * @property canWatchTask - is allowed to watch a task
 * @property canEditTaskReply - needs to be the author of specified task reply
 * @property canDeleteTaskReply - is allowed to edit the reply of a task reply
 */
export const PERMISSIONS = {
  canAccess: false,
  canEdit: false,
  canShare: false,
  canChangeStatus: false,
  canComment: false,
  canSaveVersion: false,
  canRestoreVersion: false,
  canAccessVersionHistory: false,
  canAccessAuditLog: false,
  canDeleteComment: (comment: Presentation.Data.Comment) => false,
  canEditComment: (comment: Presentation.Data.Comment) => false,
  canResolveComment: (comment: Presentation.Data.Comment) => false,
  canChangeCommentPriority: (comment: Presentation.Data.Comment) => false,
  canEditCommentReply: (reply: Presentation.Model.Comments.Reply) => false,
  canDeleteCommentReply: (reply: Presentation.Model.Comments.Reply) => false,
  canAccessTasks: false,
  canCreateTask: false,
  canDeleteTask: (task: Presentation.Data.Task) => false,
  canChangeTaskStatus: (task: Presentation.Data.Task) => false,
  canEditTask: (task: Presentation.Data.Task) => false,
  canWatchTask: false,
  canEditTaskReply: (reply: Presentation.Model.Tasks.Reply) => false,
  canDeleteTaskReply: (reply: Presentation.Model.Tasks.Reply) => false,
};

type ISuiteContext = {
  object: SuiteObject;
  permissions: typeof PERMISSIONS;
  app: SuiteProviderProps['app'];
  refetchObject: ReturnType<typeof useObject>['refetch'];
  manager: Manager;
  activeRichText?: Editor;
  setActiveRichText: (editor?: Editor) => void;
};

export type SuiteProviderProps = {
  manager: Manager;
  children: ReactNode;
  app: 'document' | 'dopdf' | 'presentation';
  /**
   * For now this is optional,
   * but when editor and pdf use the usePermissions from here, this prop should be required
   */
  hasTasks?: boolean;
};
type Manager = EditorManager | PDFManager | PresentationManager;

export const SuiteContext = createContext<ISuiteContext | undefined>(undefined);
